import React, { useState } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';

const ImageUpload = ({ value, onChange, disabled = false, required = false, error = null, multiple = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  const readFileAsDataURL = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.readAsDataURL(file);
    });
  };

  const validateFile = (file) => {
    if (!file) return false;
    if (!file.type.startsWith('image/')) return false;
    if (file.size > 5 * 1024 * 1024) return false;
    return true;
  };

  const handleFiles = async (files) => {
    const validFiles = files.filter(validateFile);
    if (validFiles.length === 0) return;

    if (multiple) {
      const results = await Promise.all(validFiles.map(readFileAsDataURL));
      const current = Array.isArray(value) ? value : [];
      onChange([...current, ...results]);
    } else {
      const result = await readFileAsDataURL(validFiles[0]);
      onChange(result);
    }
  };

  const handleFileInput = (e) => {
    handleFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (disabled) return;
    handleFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleRemove = (index) => {
    if (multiple) {
      const current = Array.isArray(value) ? value : [];
      onChange(current.filter((_, i) => i !== index));
    } else {
      onChange('');
    }
  };

  const inputId = `achievement-image-input-${multiple ? 'multi' : 'single'}`;

  // Multiple images mode
  if (multiple) {
    const images = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        {/* Lightbox modal */}
        {lightboxImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4 cursor-pointer"
            onClick={() => setLightboxImage(null)}
          >
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              className="absolute top-4 right-4 bg-white text-gray-800 rounded-full p-2 hover:bg-gray-200 transition-colors z-10"
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={lightboxImage}
              alt="Full size screenshot"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}

        {/* Existing images */}
        {images.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {images.map((img, index) => (
              <div key={index} className="relative">
                <img
                  src={img}
                  alt={`Achievement screenshot ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setLightboxImage(img)}
                />
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Upload area */}
        {!disabled && (
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
              isDragging
                ? 'border-[#1086a4] bg-blue-50'
                : error
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300 hover:border-[#1086a4]'
            } cursor-pointer`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => document.getElementById(inputId).click()}
          >
            <ImageIcon className={`mx-auto h-12 w-12 mb-3 ${error ? 'text-red-400' : 'text-gray-400'}`} />
            <p className={`text-sm font-medium mb-1 ${error ? 'text-red-700' : 'text-gray-700'}`}>
              {isDragging ? 'Drop images here' : images.length > 0 ? 'Add more screenshots' : 'Upload achievement screenshots'}
            </p>
            <p className="text-xs text-gray-500">
              PNG, JPG, GIF up to 5MB each
            </p>
            <input
              id={inputId}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileInput}
              className="hidden"
              disabled={disabled}
            />
          </div>
        )}

        {error && (
          <p className="text-red-500 text-sm">{error}</p>
        )}
      </div>
    );
  }

  // Single image mode (original behavior)
  return (
    <div className="space-y-2">
      {/* Lightbox modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <button
            type="button"
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 bg-white text-gray-800 rounded-full p-2 hover:bg-gray-200 transition-colors z-10"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={lightboxImage}
            alt="Full size screenshot"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {value ? (
        <div className="relative">
          <img
            src={value}
            alt="Achievement screenshot"
            className="w-full max-w-sm rounded-lg border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setLightboxImage(value)}
          />
          {!disabled && (
            <button
              type="button"
              onClick={() => handleRemove()}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ) : (
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragging
              ? 'border-[#1086a4] bg-blue-50'
              : error
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 hover:border-[#1086a4]'
          } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !disabled && document.getElementById(inputId).click()}
        >
          <ImageIcon className={`mx-auto h-12 w-12 mb-3 ${error ? 'text-red-400' : 'text-gray-400'}`} />
          <p className={`text-sm font-medium mb-1 ${error ? 'text-red-700' : 'text-gray-700'}`}>
            {isDragging ? 'Drop image here' : 'Upload achievement screenshot'}
          </p>
          <p className="text-xs text-gray-500">
            PNG, JPG, GIF up to 5MB
          </p>
          <input
            id={inputId}
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="hidden"
            disabled={disabled}
          />
        </div>
      )}

      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}
    </div>
  );
};

export default ImageUpload;
